import React, { useEffect, useRef, useState } from "react";

import { closeIcon as CloseIcon, linkIcon as LinkIcon } from "../../assets/icons";
import {
  VideoPreviewActions,
  VideoPreviewCaption,
  VideoPreviewCloseButton,
  VideoPreviewFile,
  VideoPreviewFooter,
  VideoPreviewFrame,
  VideoPreviewFrameWrap,
  VideoPreviewHeader,
  VideoPreviewHelp,
  VideoPreviewKicker,
  VideoPreviewName,
  VideoPreviewOpenLink,
  VideoPreviewPanel,
  VideoSplitContent,
  VideoSplitDivider,
  VideoSplitRoot,
} from "./StudentVideoSplitView.styles";

export interface StudentVideoSelection {
  id: number;
  name: string;
  playerUrl: string;
  contextLabel: string;
}

interface StudentVideoSplitViewProps {
  children: React.ReactNode;
  video: StudentVideoSelection | null;
  onClose: () => void;
}

type SplitStyle = React.CSSProperties & {
  "--student-video-left-width"?: string;
};

const MIN_LEFT_WIDTH = 420;
const MIN_RIGHT_WIDTH = 360;
const KEYBOARD_STEP = 24;
const PLAYER_COMMAND_TYPE = "lisrent-video-command";

const shouldBlockDeveloperShortcut = (event: KeyboardEvent) => {
  const key = event.key.toLowerCase();
  if (event.key === "F12") return true;
  if (event.ctrlKey && event.shiftKey && ["i", "j", "c", "k"].includes(key)) return true;
  if (event.ctrlKey && key === "u") return true;
  if (event.metaKey && event.altKey && ["i", "j", "c"].includes(key)) return true;
  return false;
};

const StudentVideoSplitView: React.FC<StudentVideoSplitViewProps> = ({
  children,
  video,
  onClose,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const videoFrameRef = useRef<HTMLIFrameElement | null>(null);
  const draggingRef = useRef(false);
  const [leftWidth, setLeftWidth] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    setLeftWidth(null);
  }, [video?.playerUrl]);

  useEffect(() => {
    if (!video) return undefined;

    const blockShortcut = (event: KeyboardEvent) => {
      if (!shouldBlockDeveloperShortcut(event)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener("keydown", blockShortcut, true);
    return () => window.removeEventListener("keydown", blockShortcut, true);
  }, [video]);

  if (!video) return <>{children}</>;

  const layoutMetrics = () => {
    const root = rootRef.current;
    if (!root) return null;
    const rect = root.getBoundingClientRect();
    const styles = window.getComputedStyle(root);
    const gap = Number.parseFloat(styles.columnGap || "0") || 0;
    const divider = root.querySelector<HTMLElement>("[data-video-split-divider]");
    const dividerWidth = divider?.getBoundingClientRect().width || 16;
    const available = Math.max(0, rect.width - dividerWidth - gap * 2);
    const maxLeft = Math.max(MIN_LEFT_WIDTH, available - MIN_RIGHT_WIDTH);
    return { rect, gap, dividerWidth, available, maxLeft };
  };

  const clampLeftWidth = (value: number) => {
    const metrics = layoutMetrics();
    if (!metrics) return value;
    return Math.max(MIN_LEFT_WIDTH, Math.min(value, metrics.maxLeft));
  };

  const setFromPointer = (clientX: number) => {
    const metrics = layoutMetrics();
    if (!metrics) return;
    const next = clientX - metrics.rect.left - metrics.gap - metrics.dividerWidth / 2;
    setLeftWidth(clampLeftWidth(next));
  };

  const currentLeftWidth = () => {
    if (leftWidth !== null) return leftWidth;
    const metrics = layoutMetrics();
    return metrics ? metrics.available / 2 : MIN_LEFT_WIDTH;
  };

  const stopDragging = (element: HTMLElement, pointerId: number) => {
    draggingRef.current = false;
    setDragging(false);
    if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
  };

  const pauseEmbeddedVideo = () => {
    videoFrameRef.current?.contentWindow?.postMessage(
      { type: PLAYER_COMMAND_TYPE, command: "pause" },
      window.location.origin,
    );
  };

  const splitStyle: SplitStyle | undefined = leftWidth === null
    ? undefined
    : { "--student-video-left-width": `${leftWidth}px` };

  return (
    <VideoSplitRoot
      ref={rootRef}
      style={splitStyle}
      onContextMenu={(event) => event.preventDefault()}
    >
      <VideoSplitContent>{children}</VideoSplitContent>

      <VideoSplitDivider
        data-video-split-divider
        $dragging={dragging}
        role="separator"
        aria-label="Изменить ширину видео"
        aria-orientation="vertical"
        aria-valuetext={leftWidth === null ? "Равные колонки" : `Левая часть ${Math.round(leftWidth)} пикселей`}
        tabIndex={0}
        onPointerDown={(event) => {
          event.preventDefault();
          draggingRef.current = true;
          setDragging(true);
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!draggingRef.current) return;
          setFromPointer(event.clientX);
        }}
        onPointerUp={(event) => stopDragging(event.currentTarget, event.pointerId)}
        onPointerCancel={(event) => stopDragging(event.currentTarget, event.pointerId)}
        onDoubleClick={() => setLeftWidth(null)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            setLeftWidth(clampLeftWidth(currentLeftWidth() - KEYBOARD_STEP));
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            setLeftWidth(clampLeftWidth(currentLeftWidth() + KEYBOARD_STEP));
          } else if (event.key === "Home") {
            event.preventDefault();
            setLeftWidth(MIN_LEFT_WIDTH);
          } else if (event.key === "Enter") {
            event.preventDefault();
            setLeftWidth(null);
          }
        }}
      />

      <VideoPreviewPanel aria-label="Предпросмотр видео">
        <VideoPreviewHeader>
          <VideoPreviewFile>
            <VideoPreviewKicker>Предпросмотр видео</VideoPreviewKicker>
            <VideoPreviewName title={video.name}>{video.name}</VideoPreviewName>
          </VideoPreviewFile>
          <VideoPreviewActions>
            <VideoPreviewOpenLink
              href={video.playerUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Открыть видео в новой вкладке"
              onClick={pauseEmbeddedVideo}
            >
              <LinkIcon aria-hidden="true" />
              <span>Открыть в новой вкладке</span>
            </VideoPreviewOpenLink>
            <VideoPreviewCloseButton
              type="button"
              onClick={onClose}
              aria-label="Закрыть предпросмотр"
            >
              <CloseIcon aria-hidden="true" />
            </VideoPreviewCloseButton>
          </VideoPreviewActions>
        </VideoPreviewHeader>

        <VideoPreviewFrameWrap>
          <VideoPreviewFrame
            ref={videoFrameRef}
            key={video.playerUrl}
            src={video.playerUrl}
            title={`Видео: ${video.name}`}
            allow="autoplay; fullscreen"
            allowFullScreen
            referrerPolicy="same-origin"
          />
        </VideoPreviewFrameWrap>

        <VideoPreviewFooter>
          <VideoPreviewCaption title={video.name}>{video.name}</VideoPreviewCaption>
          <VideoPreviewHelp>{video.contextLabel}</VideoPreviewHelp>
        </VideoPreviewFooter>
      </VideoPreviewPanel>
    </VideoSplitRoot>
  );
};

export default StudentVideoSplitView;
